import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModalState } from '../../hooks/useModalState';
import { usePositionStopOrdersByProductId } from '../../hooks/useOrders';
import { usePositions } from '../../hooks/usePositions';
import { useProducts } from '../../hooks/useProducts';
import { usePropsAsState } from '../../hooks/usePropAsState';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useHasSlTpError, useSlTpPnl } from '../../hooks/useSlTpHooks';
import { Gwei } from '../../lib/gwei';
import { OrderSide } from '../../types/generated';
import { OraclePrice } from '../OraclePrice/OraclePrice';
import { TpSlError } from '../TpSlError/TpSlError';
import { PositionStopOrderModalUi } from './PositionStopOrderModalUi';
import { usePositionTpSlMutation } from './usePositionTpSlMutation';

export const PositionStopOrderModal = () => {
  const isOpen = useModalState((state) => state.positionStopOrderModal.openOrClosed === 'open');
  const positionId = useModalState((state) => state.positionStopOrderModal.positionId);
  const setPositionStopOrderModal = useModalState((state) => state.setPositionStopOrderModal);
  const subaccount = useActiveSelectedSubaccount();
  const { data: positions } = usePositions({ subaccountId: subaccount?.id });
  const position = useMemo(
    () => positions?.find((p) => p.id === positionId),
    [positions, positionId]
  );
  const { data: products } = useProducts();
  const product = useMemo(
    () => products?.find((p) => p.id === position?.productId),
    [products, position?.productId]
  );
  const productId = position?.productId;
  const { data: stopOrdersByProductId } = usePositionStopOrdersByProductId({
    subaccountId: subaccount?.id,
    productId,
  });
  const closeOrdersForProduct = productId ? stopOrdersByProductId?.[productId] : undefined;
  const [takeProfitPrice, setTakeProfitPrice] = usePropsAsState(
    closeOrdersForProduct?.takeProfit?.stopPrice
  );
  const [stopLossPrice, setStopLossPrice] = usePropsAsState(
    closeOrdersForProduct?.stopLoss?.stopPrice
  );

  const [customTakeProfitGainPercentage, setCustomTakeProfitGainPct] = useState<
    bigint | undefined
  >();
  const [customStopLossPercentage, setCustomStopLossPercentage] = useState<bigint | undefined>();
  const [isTakeProfitFocused, setIsTakeProfitFocused] = useState(false);
  const [isStopLossFocused, setIsStopLossFocused] = useState(false);

  const { takeProfitPnlPercentage, stopLossPnlPercentage, takeProfitPnl, stopLossPnl } = useSlTpPnl(
    {
      stopLoss: stopLossPrice,
      takeProfit: takeProfitPrice,
      avgPrice: position?.avgPrice || 0n,
      side: position?.side === OrderSide.BUY ? 'long' : 'short',
      amount: position ? Gwei.abs(position.size) : 0n,
    }
  );

  // Reset inputs after closing the modal
  useEffect(() => {
    if (!isOpen) {
      setTakeProfitPrice(closeOrdersForProduct?.takeProfit?.stopPrice);
      setStopLossPrice(closeOrdersForProduct?.stopLoss?.stopPrice);
      setCustomTakeProfitGainPct(undefined);
      setCustomStopLossPercentage(undefined);
    }
  }, [
    closeOrdersForProduct?.stopLoss?.stopPrice,
    closeOrdersForProduct?.takeProfit?.stopPrice,
    isOpen,
    setStopLossPrice,
    setTakeProfitPrice,
  ]);

  const mutation = usePositionTpSlMutation(product);

  const handleSubmitClick = useCallback(() => {
    if (!position || !product) return;
    const hasTpChanged = closeOrdersForProduct?.takeProfit?.stopPrice !== takeProfitPrice;
    const hasSlChanged = closeOrdersForProduct?.stopLoss?.stopPrice !== stopLossPrice;
    if (!hasTpChanged && !hasSlChanged) {
      setPositionStopOrderModal({ positionId: undefined, openOrClosed: 'closed' });
      return;
    }
    mutation.mutate(
      {
        position,
        newTpPrice: hasTpChanged ? takeProfitPrice : undefined,
        newSlPrice: hasSlChanged ? stopLossPrice : undefined,
        existingTpOrderId: hasTpChanged ? closeOrdersForProduct?.takeProfit?.id : undefined,
        existingSlOrderId: hasSlChanged ? closeOrdersForProduct?.stopLoss?.id : undefined,
      },
      {
        onSuccess: (result) => {
          // Only close the modal if the operation indicates it should be closed
          if (result.shouldCloseModal) {
            setPositionStopOrderModal({ positionId: undefined, openOrClosed: 'closed' });
          }
        },
      }
    );
  }, [
    closeOrdersForProduct?.stopLoss?.id,
    closeOrdersForProduct?.stopLoss?.stopPrice,
    closeOrdersForProduct?.takeProfit?.id,
    closeOrdersForProduct?.takeProfit?.stopPrice,
    mutation,
    position,
    product,
    setPositionStopOrderModal,
    stopLossPrice,
    takeProfitPrice,
  ]);

  const { hasStopLossError, hasTakeProfitError } = useHasSlTpError({
    takeProfitPrice,
    stopLossPrice,
    productId: product?.id,
    side: position?.side === OrderSide.BUY ? 'long' : 'short',
  });

  if (!position || !product) return null;

  return (
    <PositionStopOrderModalUi
      isOpen={isOpen}
      setPositionStopOrderModal={setPositionStopOrderModal}
      position={position}
      product={product}
      onSubmitClick={handleSubmitClick}
      takeProfit={{
        price: takeProfitPrice,
        pnl: takeProfitPnl,
        pnlPercentage: takeProfitPnlPercentage,
        customPercentage: customTakeProfitGainPercentage,
        hasError: hasTakeProfitError,
        hasExistingOrder: Boolean(closeOrdersForProduct?.takeProfit),
        isFocused: isTakeProfitFocused,
        setPrice: setTakeProfitPrice,
        setCustomPercentage: setCustomTakeProfitGainPct,
        setIsFocused: setIsTakeProfitFocused,
      }}
      stopLoss={{
        price: stopLossPrice,
        pnl: stopLossPnl,
        pnlPercentage: stopLossPnlPercentage,
        customPercentage: customStopLossPercentage,
        hasError: hasStopLossError,
        hasExistingOrder: Boolean(closeOrdersForProduct?.stopLoss),
        isFocused: isStopLossFocused,
        setPrice: setStopLossPrice,
        setCustomPercentage: setCustomStopLossPercentage,
        setIsFocused: setIsStopLossFocused,
      }}
      OraclePriceComp={OraclePrice}
      TpSlErrorComp={TpSlError}
    />
  );
};
